// @flow
import { get, isEmpty, pick } from 'lodash';
import { resolve } from 'redux-optimist-promise';

import { apiDateToTimestamp } from 'Utils/date/apiDate';
import {
  FETCH_PROPOSALS,
  FETCH_PROPOSALS_BY_ID,
  UPDATE_ATTENDEES,
} from 'Store/booking/proposals/constants';
import { FETCH_PREBOOKING_TRAVELERS } from 'Store/prebooking-form/prebookingform.action';
import { SIGNOUT } from 'Store/user-info/actions/signout';
import { TRIP_INFORMATION_UPDATE_PARTICIPANTS_LAST_NAME } from 'Store/trip-information/tripInformation.action';
import {
  TRIP_INFORMATION_UPDATE_ADULT,
  TRIP_INFORMATION_SET_ADULTS_NUMBER,
  DEFAULT_ADULT_VALUE,
  SET_ATTENDEES,
  SET_ADULTS,
} from './constants';

export type Adults = {
  +id?: string,
  +birthdate?: ?timestamp,
  +civility?: ?string,
  +firstName?: ?string,
  +lastName?: ?string,
  +unicodeFirstName?: ?string,
  +unicodeLastName?: ?string,
  +neolid?: ?string,
};

export type State = Array<Adults>;

const initialState: State = [];

function updateAdult(state: State, action: Action<*>): State {
  const { index, valuesToUpdate } = get(action, 'payload');

  if (!Number.isFinite(index)) {
    return state;
  }

  const adults = state || [];

  return Array(index >= adults.length ? index + 1 : adults.length)
    .fill({ ...DEFAULT_ADULT_VALUE })
    .map((defaultAdult, i) => {
      if (i !== index) return adults[i] ? adults[i] : { ...DEFAULT_ADULT_VALUE };
      return { ...{ ...(adults[i] ? adults[i] : defaultAdult) }, ...valuesToUpdate };
    });
}

function setAdultsWithMainContactLastName(state: State, action: Action<*>): State {
  const { userInfos, adultCount } = action.payload;

  const { lastName } = userInfos;

  if (lastName === '') {
    return state;
  }

  const adults = state || [];

  return Array(adultCount)
    .fill({ ...DEFAULT_ADULT_VALUE })
    .map((defaultAdult, i) => {
      // First adult = mainContact so update it with userInfosData
      if (i === 0) {
        return {
          ...defaultAdult,
          ...adults[i],
          ...pick(userInfos, [
            'lastName',
            'firstName',
            'birthdate',
            'email',
            'civility',
            'unicodeFirstName',
            'unicodeLastName',
          ]),
        };
      }
      return {
        ...defaultAdult,
        ...adults[i],
        lastName:
          adults[i] && !isEmpty(adults[i].lastName) ? adults[i].lastName : userInfos.lastName,
      };
    });
}

function setAdultsNumber(state: State, action: Action<*>): State {
  const adultsNumber = get(action, 'payload.adultsNumber');

  if (adultsNumber === 0) {
    return [];
  }

  if (!Number.isFinite(adultsNumber)) {
    return state;
  }

  const adults = state || [];

  return Array(adultsNumber)
    .fill({ ...DEFAULT_ADULT_VALUE })
    .map((defaultAdult, i) => ({ ...(adults[i] ? adults[i] : defaultAdult) }));
}

const setAdults = (state: State, { payload }: Action<*>) => {
  if (!payload.adultsAttendees) {
    return state.map(adult => ({
      neolid: adult.neolid,
    }));
  }

  return state.map((adult, index) => {
    const { birthdate, homePhoneNumber, mobilePhoneNumber, ...restAttendeeData } =
      payload.adultsAttendees[index] || {};

    return {
      ...restAttendeeData,
      birthdate: birthdate ? apiDateToTimestamp(birthdate) : null,
      phoneNumber: mobilePhoneNumber || homePhoneNumber,
      neolid: adult.neolid,
    };
  });
};

export function adultsReducer(state: State = initialState, action: Action<*>): State {
  switch (get(action, 'type')) {
    case TRIP_INFORMATION_UPDATE_ADULT:
      return updateAdult(state, action);

    case TRIP_INFORMATION_UPDATE_PARTICIPANTS_LAST_NAME:
      return setAdultsWithMainContactLastName(state, action);

    case TRIP_INFORMATION_SET_ADULTS_NUMBER:
      return setAdultsNumber(state, action);

    case resolve(FETCH_PROPOSALS):
    case resolve(FETCH_PROPOSALS_BY_ID):
    case resolve(FETCH_PREBOOKING_TRAVELERS):
    case SET_ATTENDEES:
      return setAdults(state, action);
    case UPDATE_ATTENDEES: {
      if (!action.payload.attendees) {
        return state;
      }

      return state.map(adult => {
        const updatedAdult = action.payload.attendees.find(({ id }) => id === adult.id) || {};

        return {
          ...adult,
          neolid: updatedAdult.customerId,
          gmNumber: updatedAdult.gmNumber,
          mainContactId: undefined,
        };
      });
    }
    case SET_ADULTS:
      return action.payload.map((adult, index) => ({
        ...state[index],
        ...adult,
        mainContactId: adult.neolid ? undefined : adult.mainContactId,
        neolid: adult.neolid || get(state[index], 'neolid'),
        ...(adult.address
          ? {
              address: {
                ...get(state[index], 'address'),
                ...adult.address,
              },
            }
          : null),
      }));

    case SIGNOUT:
      return initialState;

    default:
      return state;
  }
}
